import { Link, useLocation, useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import { useTheme } from "next-themes";
import { Menu, Shield, X, Download } from "lucide-react";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { useAuth } from "@/contexts/AuthContext";
import { UserProfileMenu } from "./auth/UserProfileMenu";

export default function Header() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const navigation = [
    { name: "Pricing", href: "/pricing" },
    { name: "Help", href: "/help" },
  ];

  const isAdmin = useMemo(() => {
    // Simple admin check - in a real app this would check user roles/permissions
    return (
      false
    );
  }, [user]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-warm-cream-300 bg-white/80 backdrop-blur-lg">
      <nav className="max-w-7xl mx-auto px-6 lg:px-8 h-20">
        <div className="flex items-center justify-between h-full">
          {/* Logo */}
          <ContextMenu>
            <ContextMenuTrigger asChild>
              <Link to="/" className="flex items-center cursor-pointer group">
                <img
                  src={isDark ? "/logo-dark.svg" : "/logo.svg"}
                  alt="JOYLO"
                  className="h-10 lg:h-14 w-auto transition-transform group-hover:scale-105"
                />
              </Link>
            </ContextMenuTrigger>
            <ContextMenuContent className="w-64">
              <ContextMenuItem 
                onClick={() => {
                  const link = document.createElement("a");
                  link.href = "/logo.svg";
                  link.download = "logo.svg";
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                className="cursor-pointer"
              >
                <Download className="w-4 h-4 mr-2" />
                Download SVG
              </ContextMenuItem>
              <ContextMenuItem 
                onClick={() => {
                  const link = document.createElement("a");
                  link.href = "/logo.png";
                  link.download = "logo.png";
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                className="cursor-pointer"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PNG
              </ContextMenuItem>
            </ContextMenuContent>
          </ContextMenu>

          {/* Desktop Navigation */}
          {loading ? (
            <div className="hidden md:flex items-center space-x-8 h-10 w-48" />
          ) : user ? (
            <div className="flex items-center space-x-3">
              {/* Admin Panel Button */}
              {isAdmin && (
                <button
                  onClick={() => navigate("/admin")}
                  className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-xl transition-all transform hover:scale-105 shadow-lg hover:shadow-orange-500/25"
                  title="Access Admin Panel"
                >
                  <Shield className="w-4 h-4" />
                  <span className="hidden sm:inline">Admin Panel</span>
                </button>
              )}
              <UserProfileMenu />
            </div>
          ) : (
            <div className="hidden md:flex items-center space-x-8">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`text-base font-medium transition-colors ${location.pathname === item.href
                      ? "text-ink-blue-900"
                      : "text-soft-gray-600 hover:text-ink-blue-900"
                    }`}
                >
                  {item.name}
                </Link>
              ))}
              <Link
                to="/login"
                className="px-6 py-2.5 bg-gradient-sunset text-white text-sm font-semibold rounded-xl hover:opacity-90 transition-all duration-300 shadow-elegant hover:shadow-elegant-lg"
              >
                Start Free
              </Link>
            </div>
          )}

          {/* Mobile menu button — only for the logged-out nav (Pricing/Help/Start Free) below.
              Logged-in users already get the UserProfileMenu at every breakpoint above, which
              is their mobile menu; rendering this alongside it made justify-between treat it as
              a third flex item and center the profile menu instead of pinning it to the right. */}
          {!user && (
            <button
              className="md:hidden p-2 rounded-lg hover:bg-warm-cream-200 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5 text-ink-blue-900" />
              ) : (
                <Menu className="w-5 h-5 text-ink-blue-900" />
              )}
            </button>
          )}
        </div>

        {/* Mobile Navigation */}
        {!user && mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 glass border-b border-warm-cream-300 shadow-elegant-lg bg-white/90 backdrop-blur-lg">
            <div className="px-6 py-4 space-y-2">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-4 py-3 rounded-xl text-base font-medium transition-colors ${location.pathname === item.href
                      ? "bg-warm-cream-200 text-ink-blue-900"
                      : "text-soft-gray-600 hover:bg-warm-cream-100"
                    }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}

              <Link
                to="/login"
                className="block w-full mt-2 px-4 py-3 bg-gradient-sunset text-white text-base font-semibold rounded-xl hover:opacity-90 transition-all duration-300 text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                Start Free
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
